import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import LoadingSvg from 'common/images/LoadingSvg';
import colors from 'common/styles/colors';
import OrgWidgetFrame from './OrgWidgetFrame';
import { Content, EmptyText, ScrollArea } from './OrgWidgetLayout';

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const RetryButton = styled.button`
  background: none;
  border: 1px solid ${colors.greyL};
  border-radius: 4px;
  padding: 4px 14px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  color: ${colors.greyXXD};
  cursor: pointer;

  &:hover {
    background: ${colors.greyXXXL};
    border-color: ${colors.greyD};
  }
  &:focus-visible {
    outline: 2px solid #1a73e8;
    outline-offset: 2px;
  }
`;

export function OrgWidgetLoading({ height }) {
  return <LoadingSvg height={height || '120px'} />;
}

OrgWidgetLoading.propTypes = {
  height: PropTypes.string
};

OrgWidgetLoading.defaultProps = {
  height: '120px'
};

export function OrgWidgetEmpty({ message, compact, scrollable }) {
  const content = (
    <Content compact={compact}>
      <EmptyText compact={compact}>{message}</EmptyText>
    </Content>
  );

  if (scrollable === false) {
    return content;
  }

  return (
    <ScrollArea>
      {content}
    </ScrollArea>
  );
}

OrgWidgetEmpty.propTypes = {
  message: PropTypes.node.isRequired,
  compact: PropTypes.bool,
  scrollable: PropTypes.bool
};

OrgWidgetEmpty.defaultProps = {
  compact: false,
  scrollable: true
};

export function OrgWidgetError({ message, onRetry, compact, scrollable }) {
  const { t } = useTranslation();
  const content = (
    <Content compact={compact}>
      <ErrorWrap>
        <EmptyText compact={compact}>
          {message || t('orgView.error.load', "Couldn't load this data")}
        </EmptyText>
        {onRetry && (
          <RetryButton type="button" onClick={onRetry}>
            {t('orgView.error.retry', 'Retry')}
          </RetryButton>
        )}
      </ErrorWrap>
    </Content>
  );

  if (scrollable === false) {
    return content;
  }

  return (
    <ScrollArea>
      {content}
    </ScrollArea>
  );
}

OrgWidgetError.propTypes = {
  message: PropTypes.node,
  onRetry: PropTypes.func,
  compact: PropTypes.bool,
  scrollable: PropTypes.bool
};

OrgWidgetError.defaultProps = {
  message: null,
  onRetry: null,
  compact: false,
  scrollable: true
};

export function OrgWidgetDataState({
  title,
  loading,
  hasError,
  onRetry,
  errorMessage,
  isEmpty,
  loadingHeight,
  emptyMessage,
  compact,
  emptyScrollable,
  children
}) {
  if (loading) {
    return (
      <OrgWidgetFrame title={title}>
        <OrgWidgetLoading height={loadingHeight} />
      </OrgWidgetFrame>
    );
  }

  // A genuine fetch failure is distinct from "no data" — offer a retry rather
  // than a bare empty message the user can't act on.
  if (hasError) {
    return (
      <OrgWidgetFrame title={title}>
        <OrgWidgetError
          message={errorMessage}
          onRetry={onRetry}
          compact={compact}
          scrollable={emptyScrollable}
        />
      </OrgWidgetFrame>
    );
  }

  if (isEmpty) {
    return (
      <OrgWidgetFrame title={title}>
        <OrgWidgetEmpty
          message={emptyMessage}
          compact={compact}
          scrollable={emptyScrollable}
        />
      </OrgWidgetFrame>
    );
  }

  return children;
}

OrgWidgetDataState.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  hasError: PropTypes.bool,
  onRetry: PropTypes.func,
  errorMessage: PropTypes.node,
  isEmpty: PropTypes.bool.isRequired,
  loadingHeight: PropTypes.string,
  emptyMessage: PropTypes.node.isRequired,
  compact: PropTypes.bool,
  emptyScrollable: PropTypes.bool,
  children: PropTypes.node.isRequired
};

OrgWidgetDataState.defaultProps = {
  title: null,
  hasError: false,
  onRetry: null,
  errorMessage: null,
  loadingHeight: '120px',
  compact: false,
  emptyScrollable: true
};
